import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBuilding, faMapMarkerAlt, faCalendar, faServer, faFolder, faEnvelope, faPlus, faEdit, faTrash, faTimes } from "@fortawesome/pro-regular-svg-icons";
import { toast } from "react-toastify";
import LoadingIcon from "../Helpers/LoadingIcon";

export default function GridLeadSources() {
    const { current_booking } = useSelector((state) => state.booking);
    const [leadSources, setLeadSources] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingSource, setEditingSource] = useState(null);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({
        property_id: "",
        source_property_name: "",
        source_system: "",
        source_folder: "",
        source_channel: "",
        funnel_source: "",
        funnel_source_detail: "",
        notes: ""
    });

    useEffect(() => {
        if (current_booking?.id) {
            fetchLeadSources();
        }
    }, [current_booking?.id]);

    const fetchLeadSources = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}bookings/${current_booking.id}/lead-sources`);
            if (response.data.success) {
                setLeadSources(response.data.lead_sources);
            }
        } catch (error) {
            console.error("Error fetching lead sources:", error);
        } finally {
            setLoading(false);
        }
    };

    const openAddModal = () => {
        setEditingSource(null);
        setFormData({
            property_id: "",
            source_property_name: "",
            source_system: "",
            source_folder: "",
            source_channel: "",
            funnel_source: "",
            funnel_source_detail: "",
            notes: ""
        });
        setShowModal(true);
    };

    const openEditModal = (source) => {
        setEditingSource(source);
        setFormData({
            property_id: source.property_id || "",
            source_property_name: source.source_property_name || "",
            source_system: source.source_system || "",
            source_folder: source.source_folder || "",
            source_channel: source.source_channel || "",
            funnel_source: source.funnel_source || "",
            funnel_source_detail: source.funnel_source_detail || "",
            notes: source.notes || ""
        });
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingSource(null);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = {
                ...formData,
                property_id: formData.property_id || null,
                source_property_name: formData.source_property_name || null,
                source_system: formData.source_system || null,
                source_folder: formData.source_folder || null,
                source_channel: formData.source_channel || null,
                funnel_source: formData.funnel_source || null,
                funnel_source_detail: formData.funnel_source_detail || null,
                notes: formData.notes || null
            };

            if (editingSource) {
                // Update existing
                await axios.put(`${process.env.REACT_APP_API_URL}bookings/${current_booking.id}/lead-sources/${editingSource.id}`, payload);
                toast.success("Lead source updated successfully");
            } else {
                // Create new
                await axios.post(`${process.env.REACT_APP_API_URL}bookings/${current_booking.id}/lead-sources`, payload);
                toast.success("Lead source created successfully");
            }
            
            closeModal();
            fetchLeadSources();
        } catch (error) {
            console.error("Error saving lead source:", error);
            toast.error(error.response?.data?.message || "Failed to save lead source");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (sourceId) => {
        if (!window.confirm("Are you sure you want to delete this lead source?")) {
            return;
        }

        try {
            await axios.delete(`${process.env.REACT_APP_API_URL}bookings/${current_booking.id}/lead-sources/${sourceId}`);
            toast.success("Lead source deleted successfully");
            fetchLeadSources();
        } catch (error) {
            console.error("Error deleting lead source:", error);
            toast.error(error.response?.data?.message || "Failed to delete lead source");
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center py-8">
                <LoadingIcon />
            </div>
        );
    }

    return (
        <>
            {/* Add Button */}
            <div className="mb-4">
                <button
                    onClick={openAddModal}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                    <FontAwesomeIcon icon={faPlus} className="h-4 w-4" />
                    Add Lead Source
                </button>
            </div>

            {/* Lead Sources List */}
            {leadSources.length === 0 ? (
                <div className="text-center py-8">
                    <p className="text-gray-500 text-sm">No lead sources found for this booking.</p>
                    <p className="text-gray-400 text-xs mt-2">Click "Add Lead Source" to create one.</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {leadSources.map((source) => (
                        <div key={source.id} className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                            {/* Action Buttons */}
                            <div className="flex justify-end gap-2 mb-3">
                                <button
                                    onClick={() => openEditModal(source)}
                                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                    title="Edit"
                                >
                                    <FontAwesomeIcon icon={faEdit} className="h-4 w-4" />
                                </button>
                                <button
                                    onClick={() => handleDelete(source.id)}
                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                    title="Delete"
                                >
                                    <FontAwesomeIcon icon={faTrash} className="h-4 w-4" />
                                </button>
                            </div>

                            <div className="space-y-2">
                        {/* Property */}
                        {(source.property_name || source.source_property_name) && (
                            <div className="flex items-start space-x-2">
                                <FontAwesomeIcon icon={faBuilding} className="h-4 w-4 text-blue-600 mt-0.5" />
                                <div className="flex-1">
                                    <p className="text-xs text-gray-500">Property</p>
                                    <p className="text-sm font-medium text-gray-900">
                                        {source.property_name || source.source_property_name}
                                        {!source.property_name && source.source_property_name && (
                                            <span className="ml-2 text-xs text-orange-600">(Source: {source.source_property_name})</span>
                                        )}
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* AOS Units */}
                        {source.aos_unit_names && source.aos_unit_names.length > 0 && (
                            <div className="flex items-start space-x-2">
                                <FontAwesomeIcon icon={faMapMarkerAlt} className="h-4 w-4 text-green-600 mt-0.5" />
                                <div className="flex-1">
                                    <p className="text-xs text-gray-500">Units</p>
                                    <p className="text-sm font-medium text-gray-900">{source.aos_unit_names.join(", ")}</p>
                                </div>
                            </div>
                        )}

                        {/* Source System */}
                        {source.source_system && (
                            <div className="flex items-start space-x-2">
                                <FontAwesomeIcon icon={faServer} className="h-4 w-4 text-purple-600 mt-0.5" />
                                <div className="flex-1">
                                    <p className="text-xs text-gray-500">Source System</p>
                                    <p className="text-sm font-medium text-gray-900">{source.source_system}</p>
                                </div>
                            </div>
                        )}

                        {/* Source Folder */}
                        {source.source_folder && (
                            <div className="flex items-start space-x-2">
                                <FontAwesomeIcon icon={faFolder} className="h-4 w-4 text-yellow-600 mt-0.5" />
                                <div className="flex-1">
                                    <p className="text-xs text-gray-500">Source Folder</p>
                                    <p className="text-sm font-medium text-gray-900">{source.source_folder}</p>
                                </div>
                            </div>
                        )}

                        {/* Source Channel */}
                        {source.source_channel && (
                            <div className="flex items-start space-x-2">
                                <FontAwesomeIcon icon={faEnvelope} className="h-4 w-4 text-indigo-600 mt-0.5" />
                                <div className="flex-1">
                                    <p className="text-xs text-gray-500">Channel</p>
                                    <p className="text-sm font-medium text-gray-900 capitalize">{source.source_channel}</p>
                                </div>
                            </div>
                        )}

                        {/* Funnel Source */}
                        {source.funnel_source && (
                            <div className="flex items-start space-x-2">
                                <FontAwesomeIcon icon={faEnvelope} className="h-4 w-4 text-purple-600 mt-0.5" />
                                <div className="flex-1">
                                    <p className="text-xs text-gray-500">Lead Source</p>
                                    <p className="text-sm font-medium text-gray-900 capitalize">{source.funnel_source}</p>
                                </div>
                            </div>
                        )}

                        {/* Funnel Source Detail */}
                        {source.funnel_source_detail && (
                            <div className="flex items-start space-x-2">
                                <FontAwesomeIcon icon={faEnvelope} className="h-4 w-4 text-pink-600 mt-0.5" />
                                <div className="flex-1">
                                    <p className="text-xs text-gray-500">Source Detail</p>
                                    <p className="text-sm font-medium text-gray-900">{source.funnel_source_detail}</p>
                                </div>
                            </div>
                        )}

                        {/* Date */}
                        {source.source_date && (
                            <div className="flex items-start space-x-2">
                                <FontAwesomeIcon icon={faCalendar} className="h-4 w-4 text-gray-600 mt-0.5" />
                                <div className="flex-1">
                                    <p className="text-xs text-gray-500">Date</p>
                                    <p className="text-sm font-medium text-gray-900">{new Date(source.source_date).toLocaleDateString()}</p>
                                </div>
                            </div>
                        )}

                        {/* Notes */}
                        {source.notes && (
                            <div className="mt-2 pt-2 border-t border-gray-100">
                                <p className="text-xs text-gray-500">Notes</p>
                                <p className="text-xs text-gray-600 mt-1">{source.notes}</p>
                            </div>
                        )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Modal for Add/Edit */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                        {/* Modal Header */}
                        <div className="flex items-center justify-between p-6 border-b border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900">
                                {editingSource ? "Edit Lead Source" : "Add Lead Source"}
                            </h3>
                            <button
                                onClick={closeModal}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <FontAwesomeIcon icon={faTimes} className="h-5 w-5" />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="p-6 grid grid-cols-2 gap-4">
                            {/* Source Property Name */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Source Property Name
                                </label>
                                <input
                                    type="text"
                                    value={formData.source_property_name}
                                    onChange={(e) => setFormData({ ...formData, source_property_name: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-400 rounded-md shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                    placeholder="e.g., Ladybacks"
                                />
                                <p className="mt-1 text-xs text-gray-500">The property name from the source system</p>
                            </div>

                            {/* Source System */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Source System
                                </label>
                                <input
                                    type="text"
                                    value={formData.source_system}
                                    onChange={(e) => setFormData({ ...formData, source_system: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-400 rounded-md shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                    placeholder="e.g., Ladybacks, The Dawn"
                                />
                                <p className="mt-1 text-xs text-gray-500">The name of the source Romi system</p>
                            </div>

                            {/* Source Folder */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Source Folder
                                </label>
                                <input
                                    type="text"
                                    value={formData.source_folder}
                                    onChange={(e) => setFormData({ ...formData, source_folder: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-400 rounded-md shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                    placeholder="e.g., Hot Leads, Past Leads"
                                />
                                <p className="mt-1 text-xs text-gray-500">The folder on the source system</p>
                            </div>

                            {/* Source Channel */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Source Channel
                                </label>
                                <select
                                    value={formData.source_channel}
                                    onChange={(e) => setFormData({ ...formData, source_channel: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-400 rounded-md shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                >
                                    <option value="">Select channel...</option>
                                    <option value="import">Import</option>
                                    <option value="manual">Manual</option>
                                    <option value="whatsapp">WhatsApp</option>
                                    <option value="email">Email</option>
                                    <option value="phone">Phone</option>
                                    <option value="website">Website</option>
                                    <option value="referral">Referral</option>
                                </select>
                                <p className="mt-1 text-xs text-gray-500">How this lead was captured</p>
                            </div>

                            {/* Funnel Source */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Lead Source (Funnel)
                                </label>
                                <input
                                    type="text"
                                    value={formData.funnel_source}
                                    onChange={(e) => setFormData({ ...formData, funnel_source: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-400 rounded-md shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                    placeholder="e.g., email, whatsapp"
                                />
                                <p className="mt-1 text-xs text-gray-500">Original funnel source from booking metrics</p>
                            </div>

                            {/* Funnel Source Detail */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Lead Source Detail
                                </label>
                                <input
                                    type="text"
                                    value={formData.funnel_source_detail}
                                    onChange={(e) => setFormData({ ...formData, funnel_source_detail: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-400 rounded-md shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                    placeholder="e.g., sign up, website, referral"
                                />
                                <p className="mt-1 text-xs text-gray-500">Specific detail about how they found you</p>
                            </div>

                            {/* Notes */}
                            <div className="col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Notes
                                </label>
                                <textarea
                                    value={formData.notes}
                                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                    rows={3}
                                    className="w-full px-3 py-2 border border-gray-400 rounded-md shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                    placeholder="Additional notes about this lead source..."
                                />
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="flex items-center justify-end gap-2 p-6 border-t border-gray-200">
                            <button
                                onClick={closeModal}
                                disabled={saving}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                            >
                                {saving ? "Saving..." : (editingSource ? "Update" : "Create")}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
